/** Facility types offered by the FBO */
export type FacilityType = 'hangar' | 'tie_down' | 'heated_hangar'

export const FACILITY_TYPE_LABELS: Record<FacilityType, string> = {
  hangar: 'Standard Hangar',
  tie_down: 'Tie-Down (Ramp)',
  heated_hangar: 'Heated Hangar',
}

/** Base nightly rate per facility type (USD) */
export const FACILITY_BASE_RATES: Record<FacilityType, number> = {
  hangar: 85,
  tie_down: 35,
  heated_hangar: 125,
}

/** Aircraft size category for pricing */
export type PricingAircraftSize =
  | 'small_piston'
  | 'turboprop'
  | 'light_jet'
  | 'midsize_jet'
  | 'large_jet'

export const PRICING_AIRCRAFT_LABELS: Record<PricingAircraftSize, string> = {
  small_piston: 'Small Piston (Cessna 172)',
  turboprop: 'Turboprop (King Air)',
  light_jet: 'Light Jet (Citation CJ3)',
  midsize_jet: 'Midsize Jet (Challenger 350)',
  large_jet: 'Large Jet (Gulfstream G650)',
}

export const AIRCRAFT_SIZE_MULTIPLIER: Record<PricingAircraftSize, number> = {
  small_piston: 1.0,
  turboprop: 1.15,
  light_jet: 1.35,
  midsize_jet: 1.65,
  large_jet: 2.2,
}

/** Weather condition for a given day */
export type WeatherCondition = 'clear' | 'rain' | 'storm' | 'snow' | 'fog'

export const WEATHER_LABELS: Record<WeatherCondition, string> = {
  clear: 'Clear',
  rain: 'Rain',
  storm: 'Thunderstorm',
  snow: 'Snow/Ice',
  fog: 'Low Visibility',
}

export const WEATHER_MULTIPLIER: Record<WeatherCondition, number> = {
  clear: 1.0,
  rain: 1.1,
  storm: 1.25,
  snow: 1.3,
  fog: 1.05,
}

/** Pricing tier for visual color coding */
export type PricingTier = 'low' | 'standard' | 'peak'

/** A single day's pricing breakdown */
export interface DailyPriceBreakdown {
  date: string // ISO date YYYY-MM-DD
  dayLabel: string // "Mon, Feb 9"

  // Factors
  occupancyPct: number // 0-100
  weather: WeatherCondition
  isHoliday: boolean
  holidayName?: string
  isWeekend: boolean

  // Multipliers
  occupancyMultiplier: number
  weatherMultiplier: number
  holidayMultiplier: number
  aircraftMultiplier: number

  // Computed
  baseRate: number
  finalRate: number
  pricingTier: PricingTier
}

/** Summary of the full stay calculation */
export interface PriceCalculationResult {
  days: DailyPriceBreakdown[]
  totalCost: number
  averageNightlyRate: number
  lowestRate: number
  highestRate: number
  nightCount: number
  savingsVsPeak: number
  peakNights: number
  standardNights: number
  lowNights: number
}

/** Inputs selected by the user */
export interface PriceCalcInputs {
  startDate: string // ISO date
  endDate: string // ISO date
  aircraftSize: PricingAircraftSize
  facilityType: FacilityType
}
